import { useHorizontalWheelScroll } from "../../hooks/useHorizontalWheelScroll";
import { useLocalization } from "../../hooks/useLocalization";
import { IChampionSeasonStats } from "../../types/types";
import { championIconUrl } from "../../utils/championStats";
import {
  championRoleShares,
  formatCount,
  formatPercent,
  type ChampionStatsSortKey,
} from "../../utils/championstats/table";
import { csPerMinTier, dpmTier, kdaTier } from "../../utils/playerStatTiers";
import { formatStat, type SortDirection } from "../../utils/seasonstats/table";
import { PlayerRoleBadge } from "../leaderboard/PlayerRoleBadge";
import { StatValue } from "../shared/StatValue";

interface ChampionStatsTableProps {
  rows: IChampionSeasonStats[];
  sortKey: ChampionStatsSortKey;
  sortDirection: SortDirection;
  onSort: (key: ChampionStatsSortKey) => void;
  showDraftColumns: boolean;
  emptyMessage: string;
}

// Two fixed templates rather than one built at runtime, so Tailwind can see the
// arbitrary grid values while scanning the source. Track mins are sized to the
// values (and short headers); the floor is the sum of those mins plus gaps,
// not an extra ~80rem that forced scroll on typical laptops.
const FULL_GRID =
  "grid w-full min-w-[64rem] grid-cols-[2.25rem_minmax(7.5rem,1.3fr)_repeat(4,minmax(2.6rem,0.42fr))_repeat(4,minmax(3.4rem,0.58fr))_repeat(4,minmax(3.35rem,0.58fr))_minmax(3rem,0.48fr)]";

const PERFORMANCE_GRID =
  "grid w-full min-w-[36rem] grid-cols-[2.25rem_minmax(7.5rem,1.3fr)_repeat(4,minmax(3.35rem,0.7fr))_minmax(3rem,0.55fr)]";

const CHAMPION_ICON_PX = 24;

const HEADER_ROW_CLASS =
  "grid grid-cols-subgrid col-span-full min-h-[2.75rem] w-full items-center gap-x-2 gap-y-[0.65rem] border-b border-b-[var(--surface-card-border)] px-4 text-[0.72rem] font-semibold tracking-[0.03em] text-[#8b8b9a] uppercase";

const ROW_CLASS =
  "grid grid-cols-subgrid col-span-full min-h-[3.25rem] w-full items-center gap-x-2 gap-y-[0.65rem] border-b border-b-[var(--surface-card-border)] bg-[var(--surface-card-row-bg)] px-4 text-[0.92rem]";

export const ChampionStatsTable = ({
  rows,
  sortKey,
  sortDirection,
  onSort,
  showDraftColumns,
  emptyMessage,
}: ChampionStatsTableProps) => {
  const { strings } = useLocalization();
  const copy = strings.CHAMPION_STATS;
  const tableWrapRef = useHorizontalWheelScroll<HTMLDivElement>();

  const draftColumns: { key: ChampionStatsSortKey; label: string }[] = showDraftColumns
    ? [
        { key: "picks", label: copy.COLUMNS.PICKS },
        { key: "bans", label: copy.COLUMNS.BANS },
        { key: "wins", label: copy.COLUMNS.WINS },
        { key: "losses", label: copy.COLUMNS.LOSSES },
        { key: "winRate", label: copy.COLUMNS.WINRATE },
        { key: "pickPct", label: copy.COLUMNS.PICK_PCT },
        { key: "banPct", label: copy.COLUMNS.BAN_PCT },
        { key: "presence", label: copy.COLUMNS.PRESENCE },
      ]
    : [];

  const statColumn = { className: "season-stats-col-stat", numeric: true };

  const columns: {
    key: ChampionStatsSortKey | null;
    label: string;
    className: string;
    numeric?: boolean;
  }[] = [
    { key: null, label: "#", className: "season-stats-col-rank" },
    { key: "champion", label: copy.COLUMNS.CHAMPION, className: "season-stats-col-player" },
    ...draftColumns.map((column) => ({ ...column, ...statColumn })),
    { key: "kda", label: "KDA", ...statColumn },
    { key: "csPerMin", label: "CS/m", ...statColumn },
    { key: "goldPerMin", label: "GPM", ...statColumn },
    { key: "dpm", label: "DPM", ...statColumn },
    { key: "gamesCounted", label: copy.COLUMNS.GAMES, ...statColumn },
  ];

  if (!rows.length) {
    return <div className="season-stats-empty">{emptyMessage}</div>;
  }

  return (
    <div
      ref={tableWrapRef}
      className="w-full overflow-x-auto overscroll-x-contain [-webkit-overflow-scrolling:touch]"
    >
      <div
        role="table"
        aria-label={copy.TABLE_CAPTION}
        className={showDraftColumns ? FULL_GRID : PERFORMANCE_GRID}
      >
        <div role="row" className={HEADER_ROW_CLASS}>
          {columns.map((column) => {
            if (!column.key) {
              return (
                <span key={column.label} role="columnheader" className={column.className}>
                  {column.label}
                </span>
              );
            }

            const active = sortKey === column.key;
            const indicator = active ? (sortDirection === "asc" ? "↑" : "↓") : "";
            const columnKey = column.key;

            // Numeric headers sit flush right with the sort arrow on their
            // left, so the label lines up with the right-aligned values.
            const indicatorNode = (
              <span
                className={`w-3 shrink-0 text-center${active ? "" : " invisible"}`}
                aria-hidden={!active}
              >
                {indicator}
              </span>
            );

            return (
              <span
                key={columnKey}
                role="columnheader"
                aria-sort={active ? (sortDirection === "asc" ? "ascending" : "descending") : "none"}
                className={column.className}
              >
                <button
                  type="button"
                  className={`fh-ghost-btn season-stats-sort${column.numeric ? " justify-end" : ""}${active ? " season-stats-sort--active" : ""}`}
                  onClick={() => {
                    onSort(columnKey);
                  }}
                >
                  {column.numeric ? indicatorNode : null}
                  <span className="min-w-0 truncate" title={column.label}>
                    {column.label}
                  </span>
                  {column.numeric ? null : indicatorNode}
                </button>
              </span>
            );
          })}
        </div>

        {rows.map((row, index) => {
          const roles = championRoleShares(row.roles);

          return (
            <div role="row" key={row.champion} className={ROW_CLASS}>
              <span role="cell" className="season-stats-col-rank">
                {index + 1}
              </span>

              <span role="rowheader" className="flex min-w-0 items-center gap-2">
                <img
                  src={championIconUrl(row.champion)}
                  alt={row.champion}
                  width={CHAMPION_ICON_PX}
                  height={CHAMPION_ICON_PX}
                  loading="lazy"
                  className="h-6 w-6 shrink-0 rounded-[4px] object-contain"
                />
                <span className="flex min-w-0 flex-col gap-[0.15rem]">
                  <span className="overflow-hidden font-semibold text-ellipsis whitespace-nowrap text-[#e8e8ef]">
                    {row.champion}
                  </span>
                  {roles.length ? (
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      {roles.map((entry) => (
                        <span
                          key={entry.role}
                          className="inline-flex items-center gap-1 text-[0.7rem] text-[#8b8b9a] tabular-nums"
                        >
                          <PlayerRoleBadge role={entry.role} small />
                          {entry.games}
                        </span>
                      ))}
                    </span>
                  ) : null}
                </span>
              </span>

              {showDraftColumns ? (
                <>
                  <span role="cell" className="season-stats-col-stat">
                    {formatCount(row.picks)}
                  </span>
                  <span role="cell" className="season-stats-col-stat">
                    {formatCount(row.bans)}
                  </span>
                  <span role="cell" className="season-stats-col-stat">
                    {formatCount(row.wins)}
                  </span>
                  <span role="cell" className="season-stats-col-stat">
                    {formatCount(row.losses)}
                  </span>
                  <span role="cell" className="season-stats-col-stat">
                    {formatPercent(row.winRate)}
                  </span>
                  <span role="cell" className="season-stats-col-stat">
                    {formatPercent(row.pickPct)}
                  </span>
                  <span role="cell" className="season-stats-col-stat">
                    {formatPercent(row.banPct)}
                  </span>
                  <span role="cell" className="season-stats-col-stat">
                    {formatPercent(row.presence)}
                  </span>
                </>
              ) : null}

              <span role="cell" className="season-stats-col-stat">
                <StatValue
                  value={row.kda}
                  formatted={formatStat(row.kda, 2)}
                  tier={kdaTier(row.kda)}
                />
              </span>
              <span role="cell" className="season-stats-col-stat">
                <StatValue
                  value={row.csPerMin}
                  formatted={formatStat(row.csPerMin, 2)}
                  tier={csPerMinTier(row.csPerMin)}
                />
              </span>
              <span role="cell" className="season-stats-col-stat">
                <StatValue
                  value={row.goldPerMin}
                  formatted={formatStat(row.goldPerMin, 0)}
                  tier="neutral"
                />
              </span>
              <span role="cell" className="season-stats-col-stat">
                <StatValue
                  value={row.dpm}
                  formatted={formatStat(row.dpm, 0)}
                  tier={dpmTier(row.dpm)}
                />
              </span>
              <span role="cell" className="season-stats-col-stat">
                <StatValue
                  value={row.gamesCounted}
                  formatted={formatStat(row.gamesCounted, 0)}
                  tier="neutral"
                />
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
